import React, { useState, useEffect } from "react"
import {
    Box,
    Container,
    Typography,
    Button,
    Snackbar,
    Alert,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"
import CustomerForm from "../../components/customers/CustomerForm"

const CustomerCreatePage: React.FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [companyId, setCompanyId] = useState("")
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({
        open: false,
        message: "",
        severity: "success",
    })

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await apiClient.get("/api/companies")
                if (cancelled) return
                const list = res.companies || []
                if (list.length > 0) setCompanyId(list[0].id)
            } catch (e: any) {
                console.error("Failed to load companies:", e)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [])

    const handleSubmit = async (data: any) => {
        if (!companyId) return
        try {
            await adminService.createCustomer(companyId, data)
            navigate("/admin/customers", {
                state: {
                    snackbar: {
                        message: String(
                            t("customersPages.create.messages.created")
                        ),
                        severity: "success",
                    },
                },
            })
        } catch (error: any) {
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                String(t("customersPages.create.errors.createFailed"))
            setSnackbar({ open: true, message: msg, severity: "error" })
            throw error
        }
    }

    if (!companyId) {
        return (
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Container maxWidth="lg">
                    <Typography>
                        {t("customersPages.common.loading")}
                    </Typography>
                </Container>
            </Box>
        )
    }

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="lg">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/admin/customers")}
                    >
                        {t("customersPages.common.back")}
                    </Button>
                    <Typography variant="h4">
                        {t("customersPages.create.title")}
                    </Typography>
                </Box>

                <CustomerForm
                    companyId={companyId}
                    onSubmit={handleSubmit}
                    onCancel={() => navigate("/admin/customers")}
                    submitLabel={String(t("customersPages.create.submit"))}
                />
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() =>
                            setSnackbar({ ...snackbar, open: false })
                        }
                        severity={snackbar.severity}
                        variant="filled"
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    )
}

export default CustomerCreatePage
